// i18n area 'common' (TVirt task #127 layout, adopted for TVStor M1): sections
// nav, mode, language, notFound, search, home, notes, common, context, crud.
// These per-area section files are the SOURCE — add and edit keys here;
// dictionaries.ts only recomposes them into the runtime shape.

export const en = {
  nav: {
    home: 'Home',
    storage: 'Storage',

    // REMODEL R5 (ratifica §3): DUE PAGINE DISTINTE al posto della lista unica.
    cephClusters: 'Ceph clusters',
    multiscsiClusters: 'MultiSCSI clusters',
    volumes: 'Volumes',
    fileShares: 'Filesystems',

    // The layer categories of the Storage menu (dettatura Luca 2026-08-06).
    storageLayers: {
      disks: 'Disk layer',
      engine: 'Engine layer',
      logical: 'Logical layer',
      application: 'Application layer'
    },

    // Renamed 2026-08-06 (dettatura Luca): the first-level group is
    // "Backup & Replication" and its three surfaces are tabs of one page.
    protection: 'Backup & Replication',
    backupTargets: 'Destinations',
    backups: 'Backups',
    replication: 'Replication',
    tasks: 'Tasks',
    users: 'Users',

    // ENEA-B-R15 (2026-10-24): the scope console — the tenant unit.
    scopes: 'Tenants & scopes',

    // ENEA HP-SAN-1 R19 (2026-10-24): the I/O server pairs (Lustre OSS/MDS) as rbd consumers.
    ioServerPairs: 'I/O servers (Lustre)',

    // ENEA HP-SAN-3 R21 (2026-10-25): the catalog sources — filesystems already mounted on a node, catalogued.
    catalogSources: 'Catalog sources',
    credentials: 'Credentials',
    cluster: 'Cluster'
  },

  // SectionHeader micro-round (dettatura Luca 2026-08-06): ONE dry sentence
  // per console section — what you do there, in the product's own lexicon
  // (manuale-operatore §1). Shown under the section title next to the same
  // icon the menu uses.
  sectionSummary: {
    cephClusters:
      'The Ceph clusters: scale-out fabric with the explorer — health checks, daemons, every pool with its owner, the decoded CRUSH tree.',
    multiscsiClusters: 'The MultiSCSI clusters: node:vdisk members, thin pools, scrub policy and teardown.',
    volumes: "The clusters' thin volumes — snapshots, clones, online expansion; each one is LUN 0 of its target.",
    localDisks: "The node's physical disks: usability marking, chunk carving and md raids (stack L1–L3).",
    vdisks:
      'The consumable disk envelopes (L4): a raid or a jbod, LUKS2-encryptable — what storage clusters take as members.',
    remoteDisks: 'Disks exposed by third-party arrays (iSCSI/NVMe-TCP) to enlist under the chunk stack.',
    targets: 'The exports towards consumers: named iSCSI targets and hosts — per-IQN ACLs with CHAP from the keystore.',
    fileShares: 'The NFS/SMB/SFTP/FTP/TFTP file shares on top of the volumes — the unified SAN/NAS surface.',
    objectStorage: 'The S3 surface: native RGW object stores on ceph clusters, with managed buckets and accounts.',
    dhcp: 'The optional platform-managed Kea DHCPv4 service for PXE boot, anchored to a TFTP-enabled share.',
    protection:
      'Snapshot-based backups with admin-proof immutability, and asynchronous DR replication to the destinations.',
    monitoring:
      'Observability across the areas: nodes, store, clusters, volumes and shares — alerts and trends included.',
    tasks: "The platform's long-running mutations: progress, logs and the cleanup chains of failed tasks.",
    users: 'The console accounts: local users, external authentication and MFA.',
    scopes:
      'The scope tree as the tenant unit: capacity quota admitted a priori, dedicated gateway addresses, QoS per address, per-tenant report.',
    ioServerPairs:
      'Lustre I/O server pairs as native rbd consumers: one cephx per pair confined to its volumes, the credential handed once, the SAN-side fence.',
    catalogSources:
      'Filesystems already mounted on a node (a Lustre client, an NFS export, any POSIX fs) catalogued by that node into the same tables, export and SQL role as its shares — admitted from the node’s mount census.',
    credentials: 'The typed keystore: CHAP for targets, BMC accounts for fencing, and the other platform credentials.',
    cluster: 'The nodes and the cluster configuration: store, witness, networks and failover mode.'
  },
  mode: {
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    tooltip: 'Theme'
  },
  language: {
    tooltip: 'Language'
  },
  notFound: {
    title: 'Page not found',
    body: 'This address does not match any TVStor page — it may have moved with an update, or the link is stale.',
    backHome: 'Back to Home'
  },
  search: {
    tooltip: 'Search (⌘K)',
    placeholder: 'Search users, nodes…',
    empty: 'No matches'
  },

  // REFUI-1 (dettatura Luca 2026-08-06, "Home più ricca"): the Home is a real
  // dashboard — capacity per storage cluster, cluster health, open alerts, recent
  // tasks, resource counters. Absences stay honest (em dash / not yet
  // observed), never invented.
  home: {
    welcome: "You're logged in as: {name}",
    subtitle: 'Here is your TVStor cluster at a glance.',
    loadError: 'Could not load the overview — is the TVStor backend reachable?',
    capacity: {
      title: 'Cluster capacity',
      daysToFull: '≈{days} days to full',
      noForecast: 'trend not yet observed',
      empty: 'No storage clusters yet — capacity appears with the first one',
      viewAll: 'Manage the clusters'
    },
    cluster: {
      title: 'Cluster health',
      phases: {
        forming: 'Forming',
        ready: 'Ready'
      },
      store: 'Store',
      storeHealthy: 'healthy',
      storeDegraded: 'degraded',
      storePrimary: 'primary',
      storeSecondaries: '{count} secondaries',
      failover: 'Failover mode',
      witness: 'Witness',
      witnessConfigured: '{count} endpoints',
      witnessNone: 'not configured',
      nodes: 'Nodes',
      notObserved: 'not yet observed',
      viewAll: 'Open the cluster'
    },
    alerts: {
      title: 'Open alerts',
      none: 'No firing alerts',
      more: '{count} more…',
      viewAll: 'Open the monitoring'
    },
    tasks: {
      title: 'Recent tasks',
      empty: 'No tasks yet',
      viewAll: 'View all tasks'
    }
  },
  notes: {
    title: 'Notes',
    tabEdit: 'Edit',
    tabPreview: 'Preview',
    placeholder: 'Free-form operator notes — Markdown is rendered in the preview',
    empty: 'No notes',
    tooLarge: 'Notes exceed the 64 KiB limit',
    saveError: 'Could not save the notes'
  },

  // The shared vocabulary every list and dialog draws from (TVirt kept these
  // under networks.common for historical reasons; TVStor hosts them here).
  common: {
    staleHint: "The store is unreachable — showing this node's config replica; fleet state is unknown.",
    noPermission: 'You do not have permission for this action',

    // M2e (2026-08-03): the shared sudo-elevation prompt — failover, force
    // promote, fencing and witness config all ride it.
    sudoPassword: 'Your password (sudo)',
    sudoHint: 'This action is sudo-protected — confirm with your password to elevate.',
    scope: 'Scope',
    actions: 'Actions',
    create: 'Create',
    confirm: 'Confirm',
    close: 'Close',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    save: 'Save',
    yes: 'Yes',
    no: 'No',
    search: 'Search by name',
    empty: 'Nothing here yet',
    saveError: 'Could not save',
    loadError: 'Could not load — is the TVStor backend reachable?',
    createError: 'Creation failed',
    deleteError: 'Deletion failed',
    actionError: 'The action was refused',
    deleteConfirmTitle: 'Delete {name}?',
    deleteConfirmBody: 'This cannot be undone.',

    // #181 ondata 5: GET /system/health in the footer — version, answering
    // node and server clock (the backend's own facts, never the browser's).
    healthLine: 'TVStor {version} · {node}',
    healthServerTime: 'Server time: {time}',
    healthUnreachable: 'Backend unreachable'
  },
  context: {
    copySelection: 'Copy selected text'
  },
  crud: {
    create: 'Create',
    viewDetails: 'View details',
    viewOrEdit: 'View or edit',

    // BUGHUNT-8 D3 (2026-09-05): the WAIT expired, not the task — the dialog
    // says so instead of the generic refusal, with the link to the task.
    taskStillRunning: 'The wait expired but the task is still running — it may well succeed.',
    taskStillRunningLink: 'Follow it in the tasks page',

    // E2E-2c rilievo 86 (2026-11-01): the datapool's metadata lease is held by
    // another task — the dialog waits for it and retries by itself.
    leaseHeldWaiting: 'The datapool is busy: task {task} ({type}) holds its metadata lease — waiting for it, then retrying (attempt {attempt}).',
    leaseHeldLink: 'Follow the holder in the tasks page',

    // CRUD-2 (#197): the honest local-search placeholder — shown when a list
    // declares server-side filters, so the client sieve never poses as one.
    searchLocal: 'Search shown rows (local)',

    // CRUD-3 (#207): the dry-run button on forms whose verb declares
    // `?dryRun` — full server admission (schema, invariants, references,
    // RBAC, uniqueness), nothing persisted or scheduled.
    validate: 'Validate (dry run)',
    dryRunOk: 'Validation passed — nothing was created or changed',
    filterAll: 'All',
    selected: 'selected',
    clearSelection: 'Clear',
    deleteSelected: 'Delete selected',
    bulkConfirmBody: 'Delete the {count} selected items? Protected ones will be reported and kept.',
    bulkDeleted: 'deleted',
    bulkRefused: 'refused',

    // Dialog standard (TVirt round #156): the generic metadata rows every
    // details dialog closes with, and the shared immutability hints.
    fieldId: 'ID',
    createdAt: 'Created at',
    updatedAt: 'Updated at',
    immutableField: 'Fixed at creation — the contract does not allow changing it',
    immutableScope: 'The scope is assigned at creation; some resources move with a dedicated action',
    observedField: 'Observed state — reported by the platform, not editable'
  }
}

export const it: typeof en = {
  nav: {
    home: 'Home',
    storage: 'Storage',

    // REMODEL R5 (ratifica §3): DUE PAGINE DISTINTE al posto della lista unica.
    cephClusters: 'Cluster Ceph',
    multiscsiClusters: 'Cluster MultiSCSI',
    volumes: 'Volumi',
    fileShares: 'Filesystem',
    storageLayers: {
      disks: 'Livello dischi',
      engine: 'Livello engine',
      logical: 'Livello logico',
      application: 'Livello applicativo'
    },
    protection: 'Backup e replica',
    backupTargets: 'Destinazioni',
    backups: 'Backup',
    replication: 'Replica',
    tasks: 'Task',
    users: 'Utenti',

    // ENEA-B-R15 (2026-10-24): la console degli scope — l'unità tenant.
    scopes: 'Tenant e scope',

    // ENEA HP-SAN-1 R19 (2026-10-24): le coppie di server di I/O (OSS/MDS di Lustre) come consumatori rbd.
    ioServerPairs: 'Server di I/O (Lustre)',

    // ENEA HP-SAN-3 R21 (2026-10-25): le sorgenti di catalogo — filesystem già montati su un nodo, catalogati.
    catalogSources: 'Sorgenti di catalogo',
    credentials: 'Credenziali',
    cluster: 'Cluster'
  },
  sectionSummary: {
    cephClusters:
      "I cluster Ceph: fabric scale-out con l'esploratore — check di salute, demoni, ogni pool col suo proprietario, l'albero CRUSH decodificato.",
    multiscsiClusters: 'I cluster MultiSCSI: membri node:vdisk, thin pool, politica di scrub e teardown.',
    volumes: 'I volumi thin dei cluster — snapshot, cloni, espansione online; ognuno è la LUN 0 del suo target.',
    localDisks: 'I dischi fisici del nodo: marcatura di usabilità, ritaglio in chunk e raid md (stack L1–L3).',
    vdisks:
      'Gli involucri consumabili dei dischi (L4): un raid o un jbod, cifrabile LUKS2 — ciò che i cluster di storage prendono come membri.',
    remoteDisks: 'I dischi esposti da array di terze parti (iSCSI/NVMe-TCP) da arruolare sotto lo stack dei chunk.',
    targets: 'Gli export verso i consumatori: target iSCSI nominati e host — ACL per IQN con CHAP dal keystore.',
    fileShares: 'Le condivisioni file NFS/SMB/SFTP/FTP/TFTP sopra i volumi — la superficie SAN/NAS unificata.',
    objectStorage: 'La superficie S3: object store RGW nativi sui cluster ceph, con bucket e account gestiti.',
    dhcp: 'Il servizio DHCPv4 Kea opzionale gestito dalla piattaforma per il boot PXE, ancorato a una share TFTP.',
    protection: 'Backup a snapshot con immutabilità a prova di admin, e replica DR asincrona verso le destinazioni.',
    monitoring: "L'osservabilità trasversale alle aree: nodi, store, cluster, volumi e share — con alert e trend.",
    tasks: 'Le mutazioni lunghe della piattaforma: avanzamento, log e catene di cleanup dei task falliti.',
    users: 'Le utenze della console: utenti locali, autenticazione esterna e MFA.',
    scopes:
      "L'albero degli scope come unità tenant: quota di capacità ammessa a priori, indirizzi di gateway dedicati, QoS per indirizzo, report per tenant.",
    ioServerPairs:
      'Le coppie di server di I/O di Lustre come consumatori nativi rbd: una cephx per coppia confinata ai suoi volumi, la credenziale consegnata una volta, la recinzione dal lato SAN.',
    catalogSources:
      'Filesystem già montati su un nodo (un client Lustre, un export NFS, qualunque POSIX) catalogati da quel nodo nelle stesse tabelle, export e ruolo SQL delle sue share — ammessi dal censimento dei mount del nodo.',
    credentials:
      'Il keystore tipizzato: CHAP per i target, account BMC per il fencing e le altre credenziali di piattaforma.',
    cluster: 'I nodi e la configurazione del cluster: store, witness, reti e modalità di failover.'
  },
  mode: {
    light: 'Chiaro',
    dark: 'Scuro',
    system: 'Sistema',
    tooltip: 'Tema'
  },
  language: {
    tooltip: 'Lingua'
  },
  notFound: {
    title: 'Pagina non trovata',
    body: 'Questo indirizzo non corrisponde a nessuna pagina di TVStor — può essersi spostato con un aggiornamento, o il link è vecchio.',
    backHome: 'Torna alla Home'
  },
  search: {
    tooltip: 'Cerca (⌘K)',
    placeholder: 'Cerca utenti, nodi…',
    empty: 'Nessun risultato'
  },
  home: {
    welcome: "Sei entrato con l'utenza: {name}",
    subtitle: "Ecco il tuo cluster TVStor a colpo d'occhio.",
    loadError: 'Impossibile caricare la panoramica — il backend TVStor è raggiungibile?',
    capacity: {
      title: 'Capacità dei cluster',
      daysToFull: '≈{days} giorni al riempimento',
      noForecast: 'trend non ancora osservato',
      empty: 'Ancora nessun cluster di storage — la capacità compare col primo',
      viewAll: 'Gestisci i cluster'
    },
    cluster: {
      title: 'Salute del cluster',
      phases: {
        forming: 'In formazione',
        ready: 'Pronto'
      },
      store: 'Store',
      storeHealthy: 'sano',
      storeDegraded: 'degradato',
      storePrimary: 'primario',
      storeSecondaries: '{count} secondari',
      failover: 'Modalità di failover',
      witness: 'Witness',
      witnessConfigured: '{count} endpoint',
      witnessNone: 'non configurato',
      nodes: 'Nodi',
      notObserved: 'non ancora osservato',
      viewAll: 'Apri il cluster'
    },
    alerts: {
      title: 'Alert aperti',
      none: 'Nessun alert attivo',
      more: 'altri {count}…',
      viewAll: 'Apri il monitoraggio'
    },
    tasks: {
      title: 'Task recenti',
      empty: 'Ancora nessun task',
      viewAll: 'Vedi tutti i task'
    }
  },
  notes: {
    title: 'Note',
    tabEdit: 'Modifica',
    tabPreview: 'Anteprima',
    placeholder: "Note libere dell'operatore — il Markdown viene renderizzato nell'anteprima",
    empty: 'Nessuna nota',
    tooLarge: 'Le note superano il limite di 64 KiB',
    saveError: 'Salvataggio note non riuscito'
  },
  common: {
    staleHint:
      'Lo store non è raggiungibile — questa è la replica di config di questo nodo; lo stato della flotta è sconosciuto.',
    noPermission: 'Non hai il permesso per questa azione',
    sudoPassword: 'La tua password (sudo)',
    sudoHint: 'Questa azione è protetta da sudo — conferma con la tua password per elevarti.',
    scope: 'Scope',
    actions: 'Azioni',
    create: 'Crea',
    confirm: 'Conferma',
    close: 'Chiudi',
    cancel: 'Annulla',
    delete: 'Elimina',
    edit: 'Modifica',
    save: 'Salva',
    yes: 'Sì',
    no: 'No',
    search: 'Cerca per nome',
    empty: 'Ancora niente qui',
    saveError: 'Impossibile salvare',
    loadError: 'Impossibile caricare — il backend TVStor è raggiungibile?',
    createError: 'Creazione non riuscita',
    deleteError: 'Eliminazione non riuscita',
    actionError: "L'azione è stata rifiutata",
    deleteConfirmTitle: 'Eliminare {name}?',
    deleteConfirmBody: "L'operazione non è reversibile.",
    healthLine: 'TVStor {version} · {node}',
    healthServerTime: 'Ora del server: {time}',
    healthUnreachable: 'Backend non raggiungibile'
  },
  context: {
    copySelection: 'Copia testo selezionato'
  },
  crud: {
    create: 'Crea',
    viewDetails: 'Vedi dettagli',
    viewOrEdit: 'Vedi o modifica',
    taskStillRunning: "L'attesa è scaduta ma il task è ancora in corso — probabilmente riuscirà.",
    taskStillRunningLink: 'Seguilo nella pagina dei task',
    leaseHeldWaiting: 'Il datapool è occupato: il task {task} ({type}) tiene il lease dei metadati — aspetto che finisca e riprovo (tentativo {attempt}).',
    leaseHeldLink: 'Segui il detentore nella pagina dei task',
    searchLocal: 'Cerca tra le righe mostrate (locale)',
    validate: 'Verifica (dry run)',
    dryRunOk: 'Verifica superata — nulla è stato creato o modificato',
    filterAll: 'Tutti',
    selected: 'selezionati',
    clearSelection: 'Deseleziona',
    deleteSelected: 'Elimina selezionati',
    bulkConfirmBody: 'Eliminare i {count} elementi selezionati? Quelli protetti verranno segnalati e mantenuti.',
    bulkDeleted: 'eliminati',
    bulkRefused: 'rifiutati',
    fieldId: 'ID',
    createdAt: 'Creato il',
    updatedAt: 'Aggiornato il',
    immutableField: 'Fissato alla creazione — il contract non ne consente la modifica',
    immutableScope: "Lo scope si assegna alla creazione; alcune risorse si spostano con un'azione dedicata",
    observedField: 'Stato osservato — riportato dalla piattaforma, non modificabile'
  }
}
